import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserPlus, X, Lock } from 'lucide-react';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: 'sitter', // 'sitter' or 'client'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRestrictedModal, setShowRestrictedModal] = useState(false);
  const { signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match');
    }

    if (formData.password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    setLoading(true);

    try {
      await signup(formData.email, formData.password, formData.displayName, formData.userType);
      navigate('/dashboard');
    } catch (err) {
      console.error('Signup error:', err);
      if (err.message === 'ACCESS_RESTRICTED') {
        setShowRestrictedModal(true);
      } else if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please sign in instead.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setError('');
    setLoading(true);

    try {
      await loginWithGoogle(formData.userType);
      navigate('/dashboard');
    } catch (err) {
      console.error('Google signup error:', err);
      if (err.message === 'ACCESS_RESTRICTED') {
        setShowRestrictedModal(true);
      } else if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-up cancelled. Please try again.');
      } else {
        setError('Failed to sign up with Google. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pastel-pink/30 via-pastel-blue/30 to-pastel-purple/30 px-4 py-8">
      {/* Access Restricted Modal */}
      {showRestrictedModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-fade-in">
            {/* Close Button */}
            <button
              onClick={() => setShowRestrictedModal(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            {/* Icon */}
            <div className="w-16 h-16 bg-gradient-to-br from-pastel-pink to-pastel-purple rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-brand-brown text-center mb-2">
              🚧 Private Beta
            </h2>

            {/* Message */}
            <p className="text-center text-brand-brown/70 mb-4">
              PetPages is currently under development and only available to authorized testers.
            </p>

            {/* Info Box */}
            <div className="bg-pastel-pink/20 border-2 border-pastel-pink/30 rounded-xl p-4 mb-6">
              <p className="text-sm text-brand-brown/80 text-center">
                <strong>Want access?</strong><br />
                Contact the developer at<br />
                <a href="mailto:itfbusiness.com@gmail.com" className="text-pastel-purple font-medium hover:underline">
                  itfbusiness.com@gmail.com
                </a>
              </p>
            </div>

            {/* Button */}
            <button
              onClick={() => setShowRestrictedModal(false)}
              className="w-full bg-gradient-to-r from-pastel-pink to-pastel-purple text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all"
            >
              Got It
            </button>
          </div>
        </div>
      )}

      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/logo.png" 
            alt="PetPages Logo" 
            className="h-24 w-24 mx-auto mb-4 object-contain"
          />
          <h1 className="text-3xl font-bold text-brand-brown mb-2">Join PetPages</h1>
          <p className="text-brand-brown/60">Create your professional pet care account</p>
        </div>

        {/* Signup Form */}
        <div className="card p-8">
          <h2 className="text-2xl font-bold text-brand-brown mb-6 text-center">Create Account</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-xl text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* User Type Selection */}
            <div>
              <label className="block text-sm font-medium text-brand-brown mb-2">
                I am a...
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, userType: 'sitter' }))}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    formData.userType === 'sitter'
                      ? 'border-pastel-purple bg-pastel-purple/10 text-brand-brown'
                      : 'border-brand-brown/20 text-brand-brown/60 hover:border-pastel-purple/50'
                  }`}
                >
                  <span className="text-2xl mb-1 block">🐾</span>
                  <span className="font-medium">Pet Sitter</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, userType: 'client' }))}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    formData.userType === 'client'
                      ? 'border-pastel-purple bg-pastel-purple/10 text-brand-brown'
                      : 'border-brand-brown/20 text-brand-brown/60 hover:border-pastel-purple/50'
                  }`}
                >
                  <span className="text-2xl mb-1 block">👤</span>
                  <span className="font-medium">Pet Owner</span>
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-brand-brown mb-1">
                Your Name
              </label>
              <input
                id="displayName"
                name="displayName"
                type="text"
                required
                value={formData.displayName}
                onChange={handleChange}
                className="input"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-brand-brown mb-1">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="input"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-brand-brown mb-1">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="input"
                placeholder="••••••••"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-brand-brown mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="input"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center space-x-2 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-brown"></div>
                  <span>Creating account...</span>
                </>
              ) : (
                <>
                  <UserPlus className="h-5 w-5" />
                  <span>Create Account</span>
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="my-6 flex items-center">
            <div className="flex-1 border-t border-brand-brown/20"></div>
            <span className="px-4 text-sm text-brand-brown/60">or continue with</span>
            <div className="flex-1 border-t border-brand-brown/20"></div>
          </div>

          {/* Google Signup Button */}
          <button
            type="button"
            onClick={handleGoogleSignup}
            disabled={loading}
            className="w-full flex items-center justify-center space-x-3 px-6 py-3 rounded-xl border-2 border-brand-brown/20 hover:border-pastel-blue hover:bg-pastel-blue/10 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="font-medium text-brand-brown">Continue with Google</span>
          </button>

          <div className="mt-6 text-center">
            <p className="text-sm text-brand-brown/60">
              Already have an account?{' '}
              <Link to="/login" className="text-pastel-blue hover:underline font-medium">
                Sign in here
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center mt-8 text-sm text-brand-brown/50">
          © 2024 PetPages. Made with 🐾 for pet care professionals
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
